import { useEffect, useState, type ReactNode } from 'react';
import { Copy, Plus, Trash } from '@phosphor-icons/react';

import type { EconomicsResponse, EconomicsOrgRow } from '../lib/types';
import {
  aggregateEconomics,
  computeOrgEconomics,
  splitPaymentsByMethod,
  type ModelLevers,
  type OrgEconomics,
  type OrgUsage,
} from '../lib/economics';
import {
  clearScenario,
  emptyProfile,
  loadScenario,
  profileToUsage,
  saveScenario,
  scenarioCustomerCount,
  type CustomerProfile,
} from '../lib/scenario';
import { formatCurrency, formatCount, formatMonth, formatPercent } from '../lib/format';
import {
  ErrorState,
  LoadingState,
  MonthPicker,
  MonthsSelect,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useInsightsQuery,
} from '../components/ui';

type Provider = 'twilio' | 'telnyx';
/** Whose usage the model is priced against. */
type Mode = 'real' | 'scenario';

/** Margin colour thresholds — green when healthy, amber mid, red when thin/negative. */
const HEALTHY_MARGIN = 0.5;
const THIN_MARGIN = 0.2;

/** One priced customer (a real org, or a group of identical theoretical ones). */
interface PricedRow {
  key: string;
  name: string;
  /** How many customers this row represents (always 1 for a real org). */
  count: number;
  /** Usage for ONE customer of this shape. */
  usage: OrgUsage;
  /** Economics for ONE customer of this shape (margins hold for the whole group). */
  econ: OrgEconomics;
}

/**
 * Pricing & Economics — the published pricing model, priced against either
 * our real organizations or a theoretical scenario.
 *
 * Every customer pays one monthly fee with everything operational unlimited:
 * the lower price in a month with Ruta Pay (card+ACH) volume, the higher one
 * without. The only metered consumables are texts and AI credits; texts are
 * billed per billed text (up to 3 SMS segments, or one picture message) past
 * any monthly allowance. Payments are priced by method — card is a rate + flat
 * fee, bank (ACH) a pure rate capped per
 * transaction — and what payments COST us is built in (Quality Seasons' actual
 * effective JustiFi rate), never a lever. Visits and labor hours stay on the
 * page as reference stats only. Margin is an OUTPUT of the model, never an input.
 *
 * Two modes, one model:
 *  - REAL — our organizations' actual metered usage for a single month.
 *  - SCENARIO — customer profiles the operator invents ("10 like this, 25 like
 *    that"), which is the only way to answer what pricing does as we GROW.
 *
 * Switching modes changes only whose usage is priced; the levers are the same.
 */
export function PricingPage() {
  const [mode, setMode] = useState<Mode>('real');
  const [months, setMonths] = useState(6);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [provider, setProvider] = useState<Provider>('twilio');

  // Model levers — seeded from API defaults once loaded, then user-editable.
  const [levers, setLevers] = useState<EditableLevers | null>(null);

  // The theoretical scenario, restored from the last session.
  const [profiles, setProfiles] = useState<CustomerProfile[]>(() => loadScenario());
  useEffect(() => saveScenario(profiles), [profiles]);

  const economics = useInsightsQuery<EconomicsResponse>(
    ['economics', months],
    `/unit-economics?months=${months}`,
  );

  const data = economics.data;

  // Seed the levers from defaults the first time data arrives.
  if (data && levers === null) {
    setLevers(seedLevers(data));
  }

  if (economics.isLoading || !levers) return <LoadingState />;
  if (economics.error || !data) return <ErrorState error={economics.error} />;

  const availableMonths = [...data.months].sort((a, b) => b.localeCompare(a));
  const latestMonth = availableMonths[0] ?? null;
  const activeMonth =
    selectedMonth && availableMonths.includes(selectedMonth) ? selectedMonth : latestMonth;

  const scenarioCustomers = scenarioCustomerCount(profiles);
  const isScenario = mode === 'scenario';

  // In a scenario the fixed overhead is spread across the customers the scenario
  // actually has — that dilution is most of the point of simulating growth.
  const payingCustomers = isScenario
    ? Math.max(1, scenarioCustomers)
    : levers.assumedPayingCustomers;

  // Resolve provider rates → smsUnitCost / mmsUnitCost, then assemble full levers.
  // Processing costs come straight from the built-in fee model — never editable.
  const providerRate = data.defaults.providerRates[provider];
  const modelLevers: ModelLevers = {
    basePrice: levers.basePrice,
    priceWithoutPayments: levers.priceWithoutPayments,
    includedTexts: levers.includedTexts,
    includedAi: levers.includedAi,
    topUpTexts: levers.topUpTexts,
    topUpTextsPrice: levers.topUpTextsPrice,
    topUpAiCredits: levers.topUpAiCredits,
    topUpAiCreditsPrice: levers.topUpAiCreditsPrice,
    cardRatePct: levers.cardRatePct,
    cardTxnFee: levers.cardTxnFee,
    achRatePct: levers.achRatePct,
    achFeeCap: levers.achFeeCap,
    platformFixedMonthly: levers.platformFixedMonthly,
    assumedPayingCustomers: payingCustomers,
    aiUnitCost: levers.aiUnitCost,
    smsUnitCost: providerRate.sms,
    mmsUnitCost: providerRate.mms,
    cardCostPct: data.feeModel.cardCostPct,
    achCostBps: data.feeModel.achCostBps,
    achCostFixed: data.feeModel.achCostFixed,
  };

  // ---- Rows: real orgs, or the scenario's profile groups --------------------
  const rows: PricedRow[] = isScenario
    ? profiles.map((profile) => {
        const usage = profileToUsage(profile);
        return {
          key: profile.id,
          name: profile.name,
          count: Math.max(0, profile.count),
          usage,
          econ: computeOrgEconomics(usage, modelLevers),
        };
      })
    : data.organizations
        .map((org) => {
          const usage = orgUsageForMonth(org, activeMonth, levers.achVolumeSharePct);
          return {
            key: org.orgId,
            name: org.name,
            count: 1,
            usage,
            econ: computeOrgEconomics(usage, modelLevers),
          };
        })
        .sort((a, b) => b.econ.totalProfit - a.econ.totalProfit);

  // ---- Blended totals -------------------------------------------------------
  const totals = aggregateEconomics(rows.map((r) => ({ econ: r.econ, weight: r.count })));
  const sumUsage = (f: (u: OrgUsage) => number) => rows.reduce((s, r) => s + f(r.usage) * r.count, 0);
  const totalTexts = rows.reduce((s, r) => s + r.econ.billableTexts * r.count, 0);
  const totalAi = sumUsage((u) => u.aiRequests);
  const totalCardVolume = sumUsage((u) => u.cardVolume);
  const totalAchVolume = sumUsage((u) => u.achVolume);

  const monthLabel = activeMonth ? formatMonth(activeMonth) : '—';
  const customerLabel = isScenario
    ? `Simulated customers`
    : `Active orgs (${monthLabel})`;
  const customerValue = isScenario ? scenarioCustomers : data.activeOrgCount;

  return (
    <>
      <PageHeader
        title="Pricing & Economics — model"
        subtitle={
          isScenario
            ? 'A sandbox, not the books: the flat model priced against a theoretical book of customers — what growth would earn'
            : 'A sandbox, not the books: the flat model priced against our real organizations’ monthly usage'
        }
        actions={
          <>
            <MonthsSelect value={months} onChange={setMonths} />
            <MonthPicker
              months={availableMonths}
              value={selectedMonth && availableMonths.includes(selectedMonth) ? selectedMonth : null}
              onChange={setSelectedMonth}
              emptyLabel="Latest month"
            />
          </>
        }
      />

      {/* This page is a what-if tool. Say so before any number is read. */}
      <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
        <p className="text-[13px] font-semibold text-stone-900">
          Model — do not quote these figures as revenue
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-orange-800">
          Every figure here is what the pricing model <em>would</em> earn under the levers
          below, including companies we do not bill. The month we actually booked is on
          Cost&nbsp;&amp;&nbsp;margin, and the card volume behind it is on Payments.
        </p>
      </div>

      {/* Mode toggle — whose usage the model is priced against */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <ModeToggle value={mode} onChange={setMode} />
        <p className="text-xs text-stone-500">
          {isScenario
            ? 'Pricing made-up customer profiles. The levers below are unchanged — only whose usage is being priced.'
            : 'Pricing the organizations we actually have, using the selected month’s real usage.'}
        </p>
      </div>

      {/* Summary */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label={customerLabel} value={formatCount(customerValue)} />
        <StatCard label="Texts" value={formatCount(totalTexts)} hint="Billed texts (3 segments or 1 picture each)" />
        <StatCard label="AI actions" value={formatCount(totalAi)} hint="Metered AI actions" />
        <StatCard label="Cost to serve" value={formatCurrency(totals.cost)} hint="Platform share + text & AI" />
        <StatCard
          label="Subscription revenue"
          value={formatCurrency(totals.subscription)}
          hint="Monthly fee + texts"
        />
        <StatCard
          label="Payment spread"
          value={formatCurrency(totals.paymentSpread)}
          hint={`Card ${formatCurrency(totals.cardSpread)} · Bank ${formatCurrency(totals.achSpread)}`}
        />
        <StatCard
          label="Subscription margin"
          value={totals.subMargin === null ? '—' : formatPercent(totals.subMargin)}
          hint="Blended (sub − cost)/sub"
        />
        <StatCard
          label="Total margin"
          value={totals.totalMargin === null ? '—' : formatPercent(totals.totalMargin)}
          hint="Incl. payment spread"
        />
      </div>

      {/* Scenario builder */}
      {isScenario && (
        <Section
          title="Customer profiles"
          subtitle="Describe the customers you want to price. Everything is per customer, per month — the count multiplies it."
        >
          <div className="grid gap-4 lg:grid-cols-2">
            {profiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                econ={computeOrgEconomics(profileToUsage(profile), modelLevers)}
                onChange={(next) =>
                  setProfiles((prev) => prev.map((p) => (p.id === profile.id ? next : p)))
                }
                onDuplicate={() =>
                  setProfiles((prev) => {
                    const copy = { ...profile, id: newProfileId(), name: `${profile.name} copy` };
                    const at = prev.findIndex((p) => p.id === profile.id);
                    return [...prev.slice(0, at + 1), copy, ...prev.slice(at + 1)];
                  })
                }
                onRemove={
                  profiles.length > 1
                    ? () => setProfiles((prev) => prev.filter((p) => p.id !== profile.id))
                    : undefined
                }
              />
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setProfiles((prev) => [...prev, emptyProfile(newProfileId())])}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-forest"
            >
              <Plus className="h-3.5 w-3.5" weight="bold" aria-hidden />
              Add profile
            </button>
            <button
              type="button"
              onClick={() => {
                clearScenario();
                setProfiles(loadScenario());
              }}
              className="rounded-md border border-stone-300 px-3 py-1.5 text-sm font-medium text-stone-600 transition-colors hover:border-stone-400 hover:text-forest"
            >
              Reset to defaults
            </button>
            <p className="text-xs text-stone-400">
              {formatCount(scenarioCustomers)} simulated {scenarioCustomers === 1 ? 'customer' : 'customers'} · fixed
              overhead of {formatCurrency(levers.platformFixedMonthly)}/mo spreads to{' '}
              {formatCurrency(modelLevers.platformFixedMonthly / Math.max(1, payingCustomers))} each
            </p>
          </div>
        </Section>
      )}

      {/* Model levers */}
      <Section
        title="Pricing model"
        subtitle="Edit the levers — the summary and the table recompute live (seeded from current defaults)"
      >
        <LeverGroup title="Plan — monthly fee">
          <NumberField label="With Ruta Pay" value={levers.basePrice} onChange={(v) => set('basePrice', v)} prefix="$" step={25} />
          <NumberField label="Without Ruta Pay" value={levers.priceWithoutPayments} onChange={(v) => set('priceWithoutPayments', v)} prefix="$" step={25} />
        </LeverGroup>
        <p className="-mt-3 mb-5 text-[11px] text-stone-400">
          Published: $150 a month while payments run through Ruta Pay, $375 without. The first month is
          always $150; this models a later month, so a customer with no card or bank volume pays the
          higher fee — unless it had $0 revenue that month and has taken Ruta Pay payments before. AI
          overage is not sold.
        </p>

        <LeverGroup title="Texts">
          <NumberField label="Included billed texts / mo" value={levers.includedTexts} onChange={(v) => set('includedTexts', v)} step={50} />
          <NumberField label="Price per billed text" value={levers.topUpTextsPrice} onChange={(v) => set('topUpTextsPrice', v)} prefix="$" step={0.01} />
        </LeverGroup>
        <p className="-mt-3 mb-5 text-[11px] text-stone-400">
          The 200 starter texts are one-time, so a later month includes none. A billed text is up to 3
          segments, or one picture message. Sign-in codes are on Ruta and never count.
        </p>

        <div className="mb-5">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-stone-400">
            Messaging provider
          </span>
          <ProviderToggle value={provider} onChange={setProvider} />
          <p className="mt-1.5 text-[11px] text-stone-400">
            SMS {formatCurrency(providerRate.sms)} · MMS {formatCurrency(providerRate.mms)} per message
          </p>
        </div>

        <LeverGroup title="Payments — what we charge">
          <NumberField label="Card rate %" value={levers.cardRatePct} onChange={(v) => set('cardRatePct', v)} suffix="%" step={0.01} />
          <NumberField label="Card txn fee" value={levers.cardTxnFee} onChange={(v) => set('cardTxnFee', v)} prefix="$" step={0.05} />
          <NumberField label="Bank (ACH) rate %" value={levers.achRatePct} onChange={(v) => set('achRatePct', v)} suffix="%" step={0.01} />
          <NumberField label="Bank fee cap / txn" value={levers.achFeeCap} onChange={(v) => set('achFeeCap', v)} prefix="$" step={5} />
        </LeverGroup>
        <p className="-mt-3 mb-5 text-[11px] text-stone-400">
          Bank charges are a pure rate — no flat fee, capped at {formatCurrency(levers.achFeeCap)} per transaction.
          What payments <strong>cost us</strong> is built in, not a lever: card costs{' '}
          {data.feeModel.cardCostPct}% of volume — Quality Seasons' actual net effective JustiFi rate (2.39% gross less
          fee adjustments credited back) — and bank costs JustiFi's ACH rate ({data.feeModel.achCostBps} bps +{' '}
          {formatCurrency(data.feeModel.achCostFixed)}/txn). Bank has no interchange, which is why a capped bank fee
          still beats card on a large invoice.
        </p>

        <LeverGroup title="Cost basis (drives margin, never price)">
          <NumberField label="Platform fixed /mo" value={levers.platformFixedMonthly} onChange={(v) => set('platformFixedMonthly', v)} prefix="$" step={10} />
          {!isScenario && (
            <NumberField
              label="Assumed paying customers"
              value={levers.assumedPayingCustomers}
              onChange={(v) => set('assumedPayingCustomers', v)}
              step={1}
            />
          )}
          <NumberField label="AI unit cost" value={levers.aiUnitCost} onChange={(v) => set('aiUnitCost', v)} prefix="$" step={0.001} />
        </LeverGroup>
        {isScenario && (
          <p className="-mt-3 mb-5 text-[11px] text-stone-400">
            In a scenario the paying-customer count is the scenario itself —{' '}
            {formatCount(scenarioCustomers)} {scenarioCustomers === 1 ? 'customer' : 'customers'} — so adding customers
            dilutes fixed overhead automatically.
          </p>
        )}

        {!isScenario && (
          <>
            <LeverGroup title="Assumption — real orgs have no card/bank split">
              <NumberField
                label="Volume paid by bank"
                value={levers.achVolumeSharePct}
                onChange={(v) => set('achVolumeSharePct', Math.min(100, Math.max(0, v)))}
                suffix="%"
                step={5}
              />
            </LeverGroup>
            <p className="-mt-3 text-[11px] text-orange-800">
              Our payment history isn’t broken out by method yet, so this slider <strong>assumes</strong> what share of
              each org’s paid volume arrived by bank rather than card. At 0% every dollar is priced as card. Move it to
              see what happens as customers shift to ACH. Scenario mode needs no assumption — you enter card and bank
              volume directly. Volume here counts only money processed online through Ruta Pay — checks and manually
              recorded payments are excluded. Months aggregated before the online/manual split read as $0 processed
              until the revenue aggregates are rebuilt.
            </p>
          </>
        )}
      </Section>

      {/* Priced table */}
      <Section
        title={isScenario ? 'Scenario economics' : `Per-organization economics (${monthLabel})`}
        subtitle={
          isScenario
            ? 'One row per profile, showing the whole group (per-customer usage × how many). Margins hold per customer.'
            : 'One row per org using the selected month’s actual usage; sorted by total profit. Visits and labor hours are reference stats — texts and AI are the meters.'
        }
      >
        <TableScroll>
          <table className="w-full min-w-[1180px] whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-2 font-medium">{isScenario ? 'Profile' : 'Organization'}</th>
                {isScenario && <th className="py-2 text-right font-medium">Customers</th>}
                <th className="py-2 text-right font-medium">Visits</th>
                {!isScenario && <th className="py-2 text-right font-medium">Labor hrs</th>}
                <th className="py-2 text-right font-medium">Texts</th>
                <th className="py-2 text-right font-medium">AI</th>
                <th className="py-2 text-right font-medium">Card volume</th>
                <th className="py-2 text-right font-medium">Bank volume</th>
                <th className="py-2 text-right font-medium">Cost to serve</th>
                <th className="py-2 text-right font-medium">Overage</th>
                <th className="py-2 text-right font-medium">Subscription</th>
                <th className="py-2 text-right font-medium">Sub margin</th>
                <th className="py-2 text-right font-medium">Card spread</th>
                <th className="py-2 text-right font-medium">Bank spread</th>
                <th className="py-2 text-right font-medium">Total margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, name, count, usage, econ }) => (
                <tr key={key} className="border-b border-stone-100">
                  <td className="py-2 font-medium text-stone-900">{name}</td>
                  {isScenario && (
                    <td className="tabular py-2 text-right font-medium text-stone-700">{formatCount(count)}</td>
                  )}
                  <td className="tabular py-2 text-right text-stone-500">{formatCount(usage.visitCount * count)}</td>
                  {!isScenario && (
                    <td className="tabular py-2 text-right text-stone-500">
                      {formatCount(Math.round(usage.laborHours * count))}
                    </td>
                  )}
                  <td className="tabular py-2 text-right text-stone-700">{formatCount(econ.billableTexts * count)}</td>
                  <td className="tabular py-2 text-right text-stone-700">{formatCount(usage.aiRequests * count)}</td>
                  <td className="tabular py-2 text-right text-stone-500">{formatCurrency(usage.cardVolume * count)}</td>
                  <td className="tabular py-2 text-right text-stone-500">{formatCurrency(usage.achVolume * count)}</td>
                  <td className="tabular py-2 text-right text-stone-500">{formatCurrency(econ.cost * count)}</td>
                  <td className="tabular py-2 text-right text-stone-700">{formatCurrency(econ.overage * count)}</td>
                  <td className="tabular py-2 text-right font-medium text-stone-900">
                    {formatCurrency(econ.subscription * count)}
                  </td>
                  <td className={`tabular py-2 text-right font-medium ${marginColor(econ.subMargin)}`}>
                    {formatPercent(econ.subMargin)}
                  </td>
                  <td className="tabular py-2 text-right text-stone-700">{formatCurrency(econ.card.spread * count)}</td>
                  <td className="tabular py-2 text-right text-stone-700">{formatCurrency(econ.ach.spread * count)}</td>
                  <td className={`tabular py-2 text-right font-medium ${marginColor(econ.totalMargin)}`}>
                    {formatPercent(econ.totalMargin)}
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-stone-200 text-[13px] font-semibold text-stone-900">
                  <td className="py-2">Total ({rows.length})</td>
                  {isScenario && <td className="tabular py-2 text-right">{formatCount(scenarioCustomers)}</td>}
                  <td className="tabular py-2 text-right">{formatCount(sumUsage((u) => u.visitCount))}</td>
                  {!isScenario && (
                    <td className="tabular py-2 text-right">
                      {formatCount(Math.round(sumUsage((u) => u.laborHours)))}
                    </td>
                  )}
                  <td className="tabular py-2 text-right">{formatCount(totalTexts)}</td>
                  <td className="tabular py-2 text-right">{formatCount(totalAi)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totalCardVolume)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totalAchVolume)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totals.cost)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totals.overage)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totals.subscription)}</td>
                  <td className={`tabular py-2 text-right ${marginColor(totals.subMargin)}`}>
                    {totals.subMargin === null ? '—' : formatPercent(totals.subMargin)}
                  </td>
                  <td className="tabular py-2 text-right">{formatCurrency(totals.cardSpread)}</td>
                  <td className="tabular py-2 text-right">{formatCurrency(totals.achSpread)}</td>
                  <td className={`tabular py-2 text-right ${marginColor(totals.totalMargin)}`}>
                    {totals.totalMargin === null ? '—' : formatPercent(totals.totalMargin)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </TableScroll>
        {rows.length === 0 && (
          <p className="py-6 text-center text-sm text-stone-400">
            {isScenario ? 'Add a customer profile to price a scenario.' : 'No organizations in this dataset.'}
          </p>
        )}
        <p className="mt-3 text-xs text-stone-400">
          The monthly fee is {formatCurrency(modelLevers.basePrice)} with Ruta Pay volume that month,{' '}
          {formatCurrency(modelLevers.priceWithoutPayments)} without (a $0-revenue month with Ruta Pay history
          stays at the lower fee), and includes{' '}
          {formatCount(modelLevers.includedTexts)} billed texts. Each billed text past that is{' '}
          {formatCurrency(modelLevers.topUpTextsPrice)}. AI overage is not sold. Cost to serve =
          fixed platform overhead (
          {formatCurrency(modelLevers.platformFixedMonthly)}/mo ÷ {formatCount(payingCustomers)} paying{' '}
          {payingCustomers === 1 ? 'customer' : 'customers'}) + metered text &amp; AI cost. Payment spread = what the
          payer is charged − what the processor takes, for card and bank separately; the bank fee is capped at{' '}
          {formatCurrency(modelLevers.achFeeCap)} per transaction. Visits and labor hours are reference stats only;
          margins are outputs.
        </p>
      </Section>
    </>
  );

  /** Update a single lever in state. */
  function set<K extends keyof EditableLevers>(key: K, value: EditableLevers[K]) {
    setLevers((prev) => (prev ? { ...prev, [key]: value } : prev));
  }
}

/** Levers the user can edit directly (provider-derived unit costs resolved separately). */
interface EditableLevers {
  basePrice: number;
  priceWithoutPayments: number;
  includedTexts: number;
  includedAi: number;
  topUpTexts: number;
  topUpTextsPrice: number;
  topUpAiCredits: number;
  topUpAiCreditsPrice: number;
  cardRatePct: number;
  cardTxnFee: number;
  achRatePct: number;
  achFeeCap: number;
  /** Real-org mode only: assumed share of paid volume that arrived by bank. */
  achVolumeSharePct: number;
  platformFixedMonthly: number;
  assumedPayingCustomers: number;
  aiUnitCost: number;
}

function seedLevers(data: EconomicsResponse): EditableLevers {
  const d = data.defaults;
  return {
    basePrice: d.basePrice,
    priceWithoutPayments: d.priceWithoutPayments,
    includedTexts: d.includedTexts,
    includedAi: d.includedAi,
    topUpTexts: d.topUpTexts,
    topUpTextsPrice: d.topUpTextsPrice,
    topUpAiCredits: d.topUpAiCredits,
    topUpAiCreditsPrice: d.topUpAiCreditsPrice,
    cardRatePct: d.cardRatePct,
    cardTxnFee: d.cardTxnFee,
    achRatePct: d.achRatePct,
    achFeeCap: d.achFeeCap,
    achVolumeSharePct: d.achVolumeSharePct,
    platformFixedMonthly: d.platformFixedMonthly,
    assumedPayingCustomers: d.assumedPayingCustomers,
    aiUnitCost: d.aiUnitCost,
  };
}

/**
 * Pull one org's usage for a given month (zeroed if the org has no data that
 * month), splitting its paid volume into card and bank by the assumed bank share
 * — real payment history carries no method breakdown.
 */
function orgUsageForMonth(
  org: EconomicsOrgRow,
  month: string | null,
  achVolumeSharePct: number,
): OrgUsage {
  const m = month ? org.byMonth.find((x) => x.month === month) : null;
  return {
    laborHours: m?.laborHours ?? 0,
    visitCount: m?.visitCount ?? 0,
    smsSegments: m?.smsSegments ?? 0,
    mmsSegments: m?.mmsSegments ?? 0,
    aiRequests: m?.aiRequests ?? 0,
    revenue: m?.revenue ?? 0,
    hasRutaPayHistory: org.byMonth.some(
      (x) => month !== null && x.month <= month && x.paymentVolume > 0,
    ),
    ...splitPaymentsByMethod(m?.paymentVolume ?? 0, m?.paymentTxns ?? 0, achVolumeSharePct),
  };
}

let profileSeq = 0;
function newProfileId(): string {
  profileSeq += 1;
  return `profile-${Date.now().toString(36)}-${profileSeq}`;
}

function marginColor(margin: number | null): string {
  if (margin === null) return 'text-stone-500';
  if (margin >= HEALTHY_MARGIN) return 'text-primary';
  // A green→amber→red data ramp, not a warning treatment — the amber step stays.
  // amber-600 measured 3.00:1 on the #f8f8f8 canvas though, so read the darker step.
  if (margin >= THIN_MARGIN) return 'text-amber-700';
  return 'text-red-600';
}

/** One editable theoretical customer shape, with its live per-customer bill. */
function ProfileCard({
  profile,
  econ,
  onChange,
  onDuplicate,
  onRemove,
}: {
  profile: CustomerProfile;
  econ: OrgEconomics;
  onChange: (next: CustomerProfile) => void;
  onDuplicate: () => void;
  /** Undefined when this is the last profile — a scenario needs at least one. */
  onRemove?: () => void;
}) {
  const setField = <K extends keyof CustomerProfile>(key: K, value: CustomerProfile[K]) =>
    onChange({ ...profile, [key]: value });

  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
      <div className="mb-3 flex items-start gap-2">
        <label className="min-w-0 flex-1">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-stone-400">
            Profile name
          </span>
          <input
            type="text"
            value={profile.name}
            onChange={(e) => setField('name', e.target.value)}
            className="w-full rounded-md border border-stone-300 bg-white px-2.5 py-1.5 text-sm font-medium text-stone-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <div className="flex shrink-0 items-center gap-1 pt-[22px]">
          <button
            type="button"
            onClick={onDuplicate}
            aria-label={`Duplicate ${profile.name}`}
            title="Duplicate"
            className="rounded-md border border-stone-300 bg-white p-2 text-stone-500 transition-colors hover:text-forest"
          >
            <Copy className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={!onRemove}
            aria-label={`Remove ${profile.name}`}
            title={onRemove ? 'Remove' : 'A scenario needs at least one profile'}
            className="rounded-md border border-stone-300 bg-white p-2 text-stone-500 transition-colors hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-stone-500"
          >
            <Trash className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <NumberField label="How many customers" value={profile.count} onChange={(v) => setField('count', v)} step={1} />
        <NumberField label="Visits /mo" value={profile.visitCount} onChange={(v) => setField('visitCount', v)} step={50} />
        <NumberField label="AI actions /mo" value={profile.aiRequests} onChange={(v) => setField('aiRequests', v)} step={50} />
        <NumberField label="Text segments /mo" value={profile.smsSegments} onChange={(v) => setField('smsSegments', v)} step={100} />
        <NumberField label="Picture msgs /mo" value={profile.mmsSegments} onChange={(v) => setField('mmsSegments', v)} step={10} />
        <div className="hidden sm:block" />
        <NumberField label="Card volume /mo" value={profile.cardVolume} onChange={(v) => setField('cardVolume', v)} prefix="$" step={1000} />
        <NumberField label="Card payments /mo" value={profile.cardTxns} onChange={(v) => setField('cardTxns', v)} step={10} />
        <div className="hidden sm:block" />
        <NumberField label="Bank volume /mo" value={profile.achVolume} onChange={(v) => setField('achVolume', v)} prefix="$" step={1000} />
        <NumberField label="Bank payments /mo" value={profile.achTxns} onChange={(v) => setField('achTxns', v)} step={10} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-stone-200 pt-3 text-xs">
        <span className="text-stone-500">
          Each customer bills{' '}
          <span className="font-semibold text-stone-900">{formatCurrency(econ.subscription)}/mo</span>
          {econ.overage > 0 && (
            <span className="text-stone-400">
              {' '}
              ({formatCurrency(econ.overage)} in {econ.textPacks > 0 && `${econ.textPacks} text`}
              {econ.textPacks > 0 && econ.aiPacks > 0 && ' + '}
              {econ.aiPacks > 0 && `${econ.aiPacks} AI`} packs)
            </span>
          )}
        </span>
        <span className={`font-semibold ${marginColor(econ.subMargin)}`}>
          {formatPercent(econ.subMargin)} sub margin
        </span>
      </div>
    </div>
  );
}

function ModeToggle({ value, onChange }: { value: Mode; onChange: (m: Mode) => void }) {
  const options: Array<{ id: Mode; label: string }> = [
    { id: 'real', label: 'Real organizations' },
    { id: 'scenario', label: 'Simulated scenario' },
  ];
  return (
    <div className="inline-flex rounded-md border border-stone-300 bg-white p-0.5">
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.id)}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
              active ? 'bg-primary text-white shadow-sm' : 'text-stone-600 hover:text-forest'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ProviderToggle({ value, onChange }: { value: Provider; onChange: (p: Provider) => void }) {
  const options: Array<{ id: Provider; label: string }> = [
    { id: 'twilio', label: 'Twilio' },
    { id: 'telnyx', label: 'Telnyx' },
  ];
  return (
    <div className="inline-flex rounded-md border border-stone-300 p-0.5">
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
              active ? 'bg-primary text-white shadow-sm' : 'text-stone-600 hover:text-forest'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function LeverGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-5 last:mb-0">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-stone-400">{title}</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{children}</div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-stone-400">{label}</span>
      <div className="flex items-center rounded-md border border-stone-300 bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
        {prefix && <span className="pl-2.5 text-sm text-stone-400">{prefix}</span>}
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0)}
          className="tabular w-full bg-transparent px-2.5 py-1.5 text-sm text-stone-800 outline-none"
        />
        {suffix && <span className="pr-2.5 text-sm text-stone-400">{suffix}</span>}
      </div>
    </label>
  );
}
